import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import {
  Building, Calendar, User, File, Copy, MapPin, Building2, UserCheck, List, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const ICONS = {
  mitarbeiter: User,
  firma: Building,
  standort: MapPin,
  niederlassung: Building2,
  vorgesetzter: UserCheck,
  dokument: Calendar,
  urlaub: Calendar,
  liste: List,
  ma: Users,
};

const CATEGORY_LABELS = {
  mitarbeiter: 'Mitarbeiter-Daten',
  firma: 'Firmen-Daten',
  standort: 'Standort',
  niederlassung: 'Niederlassung',
  vorgesetzter: 'Vorgesetzter / Standortleiter/in',
  dokument: 'Dokument-Daten',
  urlaub: 'Urlaubs-Daten',
  liste: 'Listen-Metadaten',
  ma: 'Mitarbeiter (in Liste)',
};

const copyPlaceholder = (text) => {
  navigator.clipboard.writeText(`{{ ${text} }}`);
  toast.success('In Zwischenablage kopiert');
};

const PlaceholderItem = ({ category, field, description, prefix }) => {
  const placeholder = `${prefix || category}.${field}`;
  return (
    <button
      type="button"
      onClick={() => copyPlaceholder(placeholder)}
      className="w-full text-left px-3 py-2 rounded border border-border/40 hover:border-primary hover:bg-primary/5 transition-colors flex items-start gap-2"
    >
      <Copy className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <code className="text-xs font-mono text-primary break-all">{`{{ ${placeholder} }}`}</code>
        <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
      </div>
    </button>
  );
};

const PlaceholdersSection = ({ items, prefixOverride }) => (
  <Accordion type="multiple" defaultValue={['mitarbeiter', 'ma']} className="space-y-2">
    {Object.entries(items).map(([category, fields]) => {
      const Icon = ICONS[category] || File;
      const fieldCount = Object.keys(fields).length;
      return (
        <AccordionItem key={category} value={category} className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-primary" />
              <span className="font-medium">{CATEGORY_LABELS[category] || category}</span>
              <Badge variant="secondary" className="ml-2">{fieldCount}</Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
              {Object.entries(fields).map(([field, description]) => (
                <PlaceholderItem
                  key={field}
                  category={category}
                  field={field}
                  description={description}
                  prefix={prefixOverride?.[category] || category}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      );
    })}
  </Accordion>
);

export const PlaceholdersTab = ({ placeholders = {}, listPlaceholders = {} }) => (
  <div className="space-y-6">
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle className="font-heading">Standard-Platzhalter</CardTitle>
        <CardDescription>
          Verwenden Sie diese Platzhalter in Ihren Word- und Excel-Vorlagen.
          Format: {'{{ kategorie.feld }}'} · Klick kopiert in die Zwischenablage.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <PlaceholdersSection items={placeholders} />
      </CardContent>
    </Card>

    {Object.keys(listPlaceholders).length > 0 && (
      <Card className="border-purple-300 dark:border-purple-700">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <List className="h-5 w-5 text-purple-600" />
            Listen-Platzhalter (Mitarbeiter-Listen)
          </CardTitle>
          <CardDescription className="space-y-2">
            <p>
              Diese Platzhalter sind nur in <strong>Listen-Vorlagen</strong> verfügbar.
              Sie iterieren über alle Mitarbeiter des gewählten Bereichs.
            </p>
            <div className="bg-muted/50 rounded p-2 text-xs font-mono space-y-1 mt-2">
              <div className="text-muted-foreground"># DOCX (in einer Tabellenzeile):</div>
              <div>{'{%tr for ma in mitarbeiter_liste %}'}</div>
              <div>&nbsp;&nbsp;{'| {{ ma.name }} | {{ ma.position }} |'}</div>
              <div>{'{%tr endfor %}'}</div>
              <div className="text-muted-foreground mt-1"># XLSX: einfach eine Zeile mit {'{{ ma.feld }}'}-Zellen –</div>
              <div className="text-muted-foreground">  &nbsp;wird automatisch pro Mitarbeiter dupliziert.</div>
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PlaceholdersSection items={listPlaceholders} />
        </CardContent>
      </Card>
    )}
  </div>
);

export default PlaceholdersTab;
